import {observable, action, computed, makeObservable} from 'mobx';
import * as Sentry from '@sentry/react';
import {
  INSTRUCTION_VIEWED,
  LOCAL_STORAGE_CAMPAIGN_QUESTION_LOG,
  LOCAL_STORAGE_CAMPAIGNS_QUESTION_LOG_VERSION,
  getEmptyLocalStorageLogStructure
} from './campaign-store-constants';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import get from 'lodash/get';
import {anonymousUserId} from '../utils/data-utils';
import {posthogCapture} from '../utils/posthog-utils';

import signOffAnswerForm from 'stores/forms/sign-off-answer-form';

class CampaignStore {
  constructor() {
    makeObservable(this);
  }

  @observable activeQuestion = {};
  @observable followupQuestion = {};
  @observable isReadyToSubmit = false;
  @observable isSubmitted = false;
  @observable lastDateOfAnswerForUser = null;
  @observable isSubmissionFailureModalOpen = false;
  form = signOffAnswerForm;

  setStore = store => {
    this.store = store;
  };
  @action
  setLastDateOfAnswerForUser = val => {
    this.lastDateOfAnswerForUser = val;
  };
  @action
  setActiveQuestion = campaign => {
    this.activeQuestion = {...campaign.question, campaignId: campaign.id};
    this.lastDateOfAnswerForUser = campaign.lastDateOfAnswerForUser;
  };

  @action
  setIsReadyToSubmit = isReadyToSubmit => {
    this.isReadyToSubmit = isReadyToSubmit;
  };

  @action
  setIsSubmitted = isSubmitted => {
    this.isSubmitted = isSubmitted;
  };

  @action
  setFollowupQuestion = followupQuestion => {
    this.followupQuestion = followupQuestion;
  };

  @action
  setIsSubmissionFailureModalOpen = isSubmissionFailureModalOpen => {
    this.isSubmissionFailureModalOpen = isSubmissionFailureModalOpen;
  };

  //reset functions
  @action
  resetQuestions = () => {
    this.resetActiveQuestion();
    this.followupQuestion = {};
    this.form.reset();
  };

  @action
  resetActiveQuestion = () => {
    this.activeQuestion = {};
    this.lastDateOfAnswerForUser = null;
  };

  //computed values
  @computed
  get step() {
    if (this.isSubmitted) {
      return 'thank-you';
    }

    if (this.isReadyToSubmit) {
      return 'summary';
    }

    if (!isEmpty(this.followupQuestion)) {
      return 'follow-up-question';
    }

    if (!isEmpty(this.activeQuestion)) {
      return 'question';
    }

    return 'none';
  }

  //triggerCampaign
  triggerEvent = ({eventName, campaign, instructionId}) => {
    const {platform} = this.store;
    const localStorageLog = this.getLocalStorageLog().campaigns;

    this.resetQuestions();
    switch (eventName) {
      case INSTRUCTION_VIEWED:
        break;
      default:
        console.error('Unknown campaign event.');
    }

    if (platform.canAnswerSurveyQuestions && campaign) {
      const campaignLog = localStorageLog[campaign.id];

      if (this.shouldShowCampaign(campaign.showPeriodSeconds, instructionId, campaignLog)) {
        this.askQuestion(campaign);
      }
    }
  };

  shouldShowCampaign = (showPeriodSeconds, instructionId, campaignLog) => {
    if (!Number.isInteger(showPeriodSeconds) || !instructionId) {
      return false;
    }

    if (!campaignLog || !campaignLog.instructions[instructionId]) {
      return true;
    }

    const timeSinceLastQuestion = (Date.now() - campaignLog.instructions[instructionId]) / 1000;

    return showPeriodSeconds <= timeSinceLastQuestion;
  };

  askQuestion = campaign => {
    this.setIsReadyToSubmit(false);
    this.setIsSubmitted(false);
    this.setActiveQuestion(campaign);
  };

  getLocalStorageLog = () => {
    const localStorageLog = JSON.parse(localStorage.getItem(LOCAL_STORAGE_CAMPAIGN_QUESTION_LOG));

    return !localStorageLog || !this.checkLocalStorageStructure(localStorageLog)
      ? getEmptyLocalStorageLogStructure()
      : localStorageLog;
  };

  checkLocalStorageStructure = localStorageLog => {
    if (
      !localStorageLog.campaigns ||
      !localStorageLog.campaigns.version ||
      localStorageLog.campaigns.version !== LOCAL_STORAGE_CAMPAIGNS_QUESTION_LOG_VERSION
    )
      return false;

    const recordedCampaigns = Object.values(omit(localStorageLog.campaigns, ['version']));

    return recordedCampaigns.every(campaign => campaign.instructions);
  };

  //answer question
  @action
  answerQuestion = () => {
    if (get(this, 'store.analytics.posthogIsLoaded', false)) {
      posthogCapture('Signoff Question Answered');
    }

    const followupQuestion = this.pickFollowup({
      followUps: this.activeQuestion.followUps,
      answer: this.form.$('answer').value
    });

    if (followupQuestion) {
      this.followupQuestion = followupQuestion;
    } else {
      this.setIsReadyToSubmit(true);
    }
  };

  updateLocalStorageLog({campaignId, instructionId}) {
    const currentLog = this.getLocalStorageLog();
    const timestamp = Date.now();
    const updatedLog = {...currentLog};
    if (!updatedLog.campaigns[campaignId]) {
      updatedLog.campaigns[campaignId] = {instructions: {}};
    }
    updatedLog.campaigns[campaignId].instructions[instructionId] = timestamp;

    localStorage.setItem(LOCAL_STORAGE_CAMPAIGN_QUESTION_LOG, JSON.stringify(updatedLog));
  }

  pickFollowup = ({followUps, answer}) => {
    const followUp =
      followUps &&
      followUps.find((q, idx) => {
        if (q.rangeFrom <= answer && answer <= q.rangeTo) {
          q.idx = idx;
          return true;
        }

        return false;
      });
    return followUp;
  };

  answerFollowup = () => {
    this.setIsReadyToSubmit(true);
  };

  submitSignOff = ({instructionId, mutation, nextButton}) => {
    if (get(this, 'store.analytics.posthogIsLoaded', false)) {
      posthogCapture('Click Submit Signoff');
    }

    const {campaignId, locale} = this.activeQuestion;
    const {answer, followUpAnswer, user} = this.form.values();
    const {instructionPage, platform} = this.store;
    this.updateLocalStorageLog({campaignId, instructionId});

    mutation({
      answer: {
        anonymousUser: anonymousUserId(),
        answer,
        campaignQuestionLocale: locale,
        followUpAnswer,
        guideVersionId: instructionPage.currentGuideVersionId,
        instructionId,
        questionId: campaignId,
        user,
        userPlatformLocale: platform.getLanguage()
      }
    }).then(
      () => {
        this.setIsReadyToSubmit(false);
        this.setIsSubmitted(true);
        this.resetQuestions();

        if (nextButton) {
          nextButton.click();
        }
      },
      error => {
        Sentry.captureException(error);
        this.setIsSubmissionFailureModalOpen(true);
      }
    );
  };
}

export default CampaignStore;
